import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../Components/Sidebar";
import BottomBar from "../Components/BottomBar";
import { Toaster, toast } from "react-hot-toast";
import { createCandidate, updateCandidate, getCandidatesByElection } from "../services/CandidateService";
import { getAllElections } from "../services/ElectionService";
import { Menu } from "lucide-react";
import TopBar from "../Components/TopBar"; // Import Menu icon for mobile menu toggle

const RegisterCandidatePage = () => {
    // When editing, candidate data is passed via location.state.
    const location = useLocation();
    const candidateData = location.state;
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);

    const [elections, setElections] = useState([]);
    const [selectedElection, setSelectedElection] = useState(candidateData ? candidateData.electionId : "");

    // Track sidebar state and handle window resize
    useEffect(() => {
        // Handle responsive sidebar on window resize
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 768);
        };

        window.addEventListener('resize', handleResize);

        // Listen for sidebar toggle events from Sidebar component
        const handleSidebarToggle = (event) => {
            if (event.detail && typeof event.detail.expanded === "boolean") {
                setIsSidebarOpen(event.detail.expanded);
            }
        };

        window.addEventListener("sidebarToggle", handleSidebarToggle);

        // Cleanup listeners
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener("sidebarToggle", handleSidebarToggle);
        };
    }, []);

    // Fetch elections list on mount.
    useEffect(() => {
        getAllElections()
            .then((response) => setElections(response))
            .catch(() => toast.error("Failed to load elections"));
    }, []);

    const [formData, setFormData] = useState({
        name: candidateData ? candidateData.nameOfCandidate || "" : "",
        designation: candidateData ? candidateData.currentDesignation || "" : "",
        email: candidateData ? candidateData.email || "" : "",
        position: candidateData ? candidateData.electionPosition || "" : "",
        age: candidateData ? candidateData.age || "" : "",
        NIC: candidateData ? candidateData.NIC || candidateData.nic || "" : "",
        employeeId: candidateData ? candidateData.empId || "" : "",
        gender: candidateData ? candidateData.gender || "" : "",
        photo: null,
        electionId: candidateData ? candidateData.electionId || "" : "",
    });

    const [formErrors, setFormErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleFileChange = (e) => {
        setFormData((prev) => ({ ...prev, photo: e.target.files[0] }));
        if (formErrors.photo) {
            setFormErrors((prev) => ({ ...prev, photo: "" }));
        }
    };

    const handleElectionChange = (e) => {
        const electionId = e.target.value;
        setSelectedElection(electionId);
        setFormData((prev) => ({ ...prev, electionId }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = "Candidate name is required.";
        if (!formData.designation.trim()) errors.designation = "Designation is required.";
        if (!formData.email.trim()) {
            errors.email = "Email is required.";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) errors.email = "Invalid email address.";
        }
        if (!formData.position.trim()) errors.position = "Position is required.";
        if (!formData.age.toString().trim()) {
            errors.age = "Age is required.";
        } else if (Number(formData.age) <= 0) {
            errors.age = "Age must be a positive number.";
        }
        if (!formData.NIC.trim()) errors.NIC = "NIC is required.";
        if (!formData.employeeId.trim()) errors.employeeId = "Employee ID is required.";
        if (!formData.gender.trim()) errors.gender = "Gender is required.";
        if (!selectedElection) errors.electionId = "Please select an election.";
        if (!candidateData && !formData.photo) errors.photo = "Candidate photo is required.";
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        // Check candidate limit for the selected election.
        try {
            const election = elections.find((ev) => ev.id === selectedElection);
            if (!election) {
                toast.error("Invalid election selected.");
                return;
            }
            const currentCandidates = await getCandidatesByElection(selectedElection);
            if (!candidateData && currentCandidates.length >= election.candidateCount) {
                toast.error("Candidate limit for this election has been reached.");
                return;
            }
        } catch (error) {
            toast.error("Error checking candidate limit.");
            return;
        }

        const data = new FormData();
        data.append("empId", formData.employeeId);
        data.append("nameOfCandidate", formData.name);
        data.append("currentDesignation", formData.designation);
        data.append("email", formData.email);
        data.append("electionPosition", formData.position);
        data.append("age", formData.age);
        data.append("NIC", formData.NIC);
        data.append("gender", formData.gender);
        data.append("electionId", selectedElection);
        if (formData.photo) {
            data.append("file", formData.photo);
        }
        try {
            if (candidateData) {
                await updateCandidate(candidateData.id, data);
                toast.success("Candidate updated successfully!");
            } else {
                await createCandidate(data);
                toast.success("Candidate registered successfully!");
            }
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    const handleClear = () => {
        setFormData({
            name: "",
            designation: "",
            email: "",
            position: "",
            age: "",
            NIC: "",
            employeeId: "",
            gender: "",
            photo: null,
            electionId: "",
        });
        setSelectedElection("");
        setFormErrors({});
    };

    const toggleSidebar = () => {
        const newState = !isSidebarOpen;
        setIsSidebarOpen(newState);
        // Dispatch the custom event to sync with Sidebar component
        window.dispatchEvent(
            new CustomEvent("sidebarToggle", { detail: { expanded: newState } })
        );
    };

    // Updated contentClass to properly center content when sidebar is collapsed
    const contentClass = isSidebarOpen
        ? "sm:ml-64 w-full sm:w-[calc(100%-16rem)]"
        : "ml-16 w-[calc(100%-4rem)] md:w-[calc(100%-4rem)] mx-auto flex justify-center";

    // Inner container class for centering content
    const innerContainerClass = !isSidebarOpen
        ? "w-full max-w-4xl"
        : "w-full";

    return (
        <div style={{ fontFamily: "Montserrat, sans-serif" }} className="flex flex-col h-screen bg-bgColor relative">
            <Toaster />

            {/* Header with menu toggle */}
            <header className="flex items-center justify-between px-4 py-3 md:px-6 md:py-4 fixed top-0 w-full z-30">
                <button
                    onClick={toggleSidebar}
                    className="p-2 text-white"
                    aria-label="Toggle sidebar"
                >
                    <Menu size={24} />
                </button>
                <TopBar/>
            </header>

            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className={`flex-1 transition-all duration-300 mt-16 ${contentClass}`}>
                <div className={`p-3 md:p-10 h-[calc(100vh-4rem)] overflow-auto ${innerContainerClass}`}>
                    <h2 className="text-lg md:text-xl text-black font-bold mt-6 md:mt-10">
                        {candidateData ? "Edit Candidate" : "Register Candidate"}
                    </h2>
                    <div className="bg-registerForm text-black rounded-lg shadow-lg p-4 md:p-8 w-full mt-4 md:mt-6">
                        <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit}>
                            {/* Election Selection */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Select Election</label>
                                <select
                                    name="electionId"
                                    value={selectedElection}
                                    onChange={handleElectionChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                >
                                    <option value="">-- Select an Election --</option>
                                    {elections.map((election) => (
                                        <option key={election.id} value={election.id}>
                                            {election.name} ({election.electionDate})
                                        </option>
                                    ))}
                                </select>
                                {formErrors.electionId && <p className="text-red-500 text-xs mt-1">{formErrors.electionId}</p>}
                            </div>
                            {/* Name */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Name of the candidate</label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Enter candidate's name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                />
                                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                            </div>
                            {/* Designation */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Current Designation</label>
                                <input
                                    type="text"
                                    name="designation"
                                    placeholder="Enter current designation"
                                    value={formData.designation}
                                    onChange={handleChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                />
                                {formErrors.designation && <p className="text-red-500 text-xs mt-1">{formErrors.designation}</p>}
                            </div>
                            {/* Email */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Email</label>
                                <input
                                    type="text"
                                    name="email"
                                    placeholder="Enter email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                />
                                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                            </div>
                            {/* Position */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Position Contested in the Election</label>
                                <input
                                    type="text"
                                    name="position"
                                    placeholder="Enter position contested"
                                    value={formData.position}
                                    onChange={handleChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                />
                                {formErrors.position && <p className="text-red-500 text-xs mt-1">{formErrors.position}</p>}
                            </div>
                            {/* Age */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Age</label>
                                <input
                                    type="number"
                                    name="age"
                                    placeholder="Enter age"
                                    value={formData.age}
                                    onChange={handleChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                />
                                {formErrors.age && <p className="text-red-500 text-xs mt-1">{formErrors.age}</p>}
                            </div>
                            {/* NIC */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">National Identity Card Number</label>
                                <input
                                    type="text"
                                    name="NIC"
                                    placeholder="Enter NIC number"
                                    value={formData.NIC}
                                    onChange={handleChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                />
                                {formErrors.NIC && <p className="text-red-500 text-xs mt-1">{formErrors.NIC}</p>}
                            </div>
                            {/* Employee ID */}
                            <div>
                                <label className="block text-xs md:text-sm font-semibold mb-1">Employee ID</label>
                                <input
                                    type="text"
                                    name="employeeId"
                                    placeholder="Enter employee ID"
                                    value={formData.employeeId}
                                    onChange={handleChange}
                                    className="w-full border-b border-gray-400 focus:outline-none focus:border-black bg-transparent text-sm md:text-base py-1"
                                />
                                {formErrors.employeeId && <p className="text-red-500 text-xs mt-1">{formErrors.employeeId}</p>}
                            </div>
                            {/* Photo and Gender */}
                            <div className="flex flex-col md:flex-row md:justify-start md:space-x-8 space-y-4 md:space-y-0 mt-4">
                                <div className="flex flex-col">
                                    <label className="block text-xs md:text-sm font-semibold mb-2">Add Photo</label>
                                    <div className="relative">
                                        <label
                                            htmlFor="photo-upload"
                                            className="bg-gray-200 text-gray-600 font-medium py-1 md:py-2 px-3 md:px-4 text-sm rounded-lg cursor-pointer shadow-sm hover:bg-gray-300 transition"
                                        >
                                            + Choose File
                                        </label>
                                        <input onChange={handleFileChange} id="photo-upload" type="file" className="hidden" />
                                    </div>
                                    {formErrors.photo && <p className="text-red-500 text-xs mt-1">{formErrors.photo}</p>}
                                </div>
                                <div className="flex flex-col">
                                    <label className="block text-xs md:text-sm font-semibold mb-2">Gender</label>
                                    <select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleChange}
                                        className="bg-gray-200 text-gray-600 font-medium py-1 md:py-2 px-4 md:px-8 text-sm rounded-lg cursor-pointer shadow-sm hover:bg-gray-300 transition"
                                    >
                                        <option value="">Select</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                    {formErrors.gender && <p className="text-red-500 text-xs mt-1">{formErrors.gender}</p>}
                                </div>
                            </div>
                            {/* Photo Preview */}
                            {formData.photo ? (
                                <div className="mt-4">
                                    <p className="text-xs md:text-sm font-semibold">Photo Preview:</p>
                                    <img
                                        src={URL.createObjectURL(formData.photo)}
                                        alt="Candidate Preview"
                                        className="w-32 md:w-48 h-auto mt-2 border rounded"
                                    />
                                </div>
                            ) : (
                                candidateData &&
                                candidateData.imageUrl && (
                                    <div className="mt-4">
                                        <p className="text-xs md:text-sm font-semibold">Current Photo:</p>
                                        <img
                                            src={candidateData.imageUrl}
                                            alt="Candidate Stored"
                                            className="w-32 md:w-48 h-auto mt-2 border rounded"
                                        />
                                    </div>
                                )
                            )}
                            {/* Buttons */}
                            <div className="flex justify-center md:justify-end w-full space-x-3 md:space-x-4 mt-6">
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="flex items-center bg-gray-300 text-black text-sm md:text-base font-semibold rounded-full px-4 md:px-6 py-1.5 md:py-2 hover:bg-gray-400 transition"
                                >
                                    Clear
                                </button>
                                <button
                                    type="submit"
                                    className="flex items-center bg-black text-white text-sm md:text-base font-semibold rounded-full px-4 md:px-6 py-1.5 md:py-2 hover:bg-gray-300 hover:text-black transition"
                                >
                                    Confirm
                                    <div className="ml-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="lucide lucide-circle-check md:w-6 md:h-6"
                                        >
                                            <circle cx="10" cy="10" r="8" />
                                            <path d="m7 10 2 2 4-4" />
                                        </svg>
                                    </div>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <BottomBar />
        </div>
    );
};

export default RegisterCandidatePage;